import { takeLatest, take, fork, put, select, call } from "redux-saga/effects";
import { delay } from "redux-saga";
import { push } from "redux-little-router";
import firebaseapp, {
  refreshToken,
  sendVerificationEmail as sendFirebaseVerificationEmail,
} from "../services/firebase";
import mixpanel from "../services/mixpanel";
import firebase from "firebase";
import { showAlert } from "../actions";
import {
  isCreatingProfile,
  sendVerificationEmail,
  hideOnBoardingDimmer,
  hideProfileUpdateDimmer,
  showOnBoardingDimmer,
  showProfileUpdateDimmer,
  hideEmailVerificationBanner,
  setUser,
  setRawUser,
  setisVerificationCodeVerified,
} from "../actions/userActions";
import { selectUser, selectIsSSRUser } from "../util/selectors";
import { getProfileOfFirebaseUser } from "../util";
import IS_CLIENT_RENDERED from "../util/isClientRendered";
import api from "../api/api";
import UserApi from "../api/UserApi";
import { startSubmit, stopSubmit } from "redux-form";
import { ONBOARDING_FIELDS } from "../components/OnBoardingDimmer/constants";

const LOGIN_WITH_FACEBOOK_ACTION_BASE = "LOGIN_WITH_FACEBOOK";
const LOGIN_WITH_EMAIL_AND_PASSWORD_ACTION_BASE = "LOGIN_WITH_EMAIL_AND_PASSWORD";
const SIGNUP_WITH_EMAIL_AND_PASSWORD_ACTION_BASE = "SIGNUP_WITH_EMAIL_AND_PASSWORD";
const SEND_PASSWORD_RESET_EMAIL_ACTION_BASE = "SEND_PASSWORD_RESET_EMAIL";
const VERIFY_PASSWORD_RESET_CODE_ACTION_BASE = "VERIFY_PASSWORD_RESET_CODE";
const APPLY_VERIFY_EMAIL_ACTION_CODE_ACTION_BASE = "APPLY_VERIFY_EMAIL_ACTION_CODE";
const CHANGE_PASSWORD_ACTION_BASE = "CHANGE_PASSWORD";
const SIGNOUT_ACTION_BASE = "SIGNOUT";
const SEND_VERIFICATION_EMAIL_ACTION_BASE = "SEND_VERIFICATION_EMAIL";
const CREATE_PROFILE_ACTION_BASE = "CREATE_PROFILE";
const UPDATE_PROFILE_ACTION_BASE = "UPDATE_PROFILE";
const FETCH_PROFILE_ACTION_BASE = "FETCH_USER_EXTRA_PROFILE_DATA";

function* handleSetRawUser({ user }) {
  let token = user.toJSON().stsTokenManager.accessToken;
  const profile = getProfileOfFirebaseUser(user, token);
  yield put(setUser(profile));
}

function* handleLoginWithFacebookRequest() {
  let provider = new firebase.auth.FacebookAuthProvider();
  yield mixpanel.track("login with facebook");
  // when firebase auth returns errors it will be now redirected to /signin
  yield put(push("/signin"));
  yield firebaseapp.auth().signInWithRedirect(provider);
}

function* handleLoginWithEmailAndPasswordRequest({ credentials }) {
  const { email, password } = credentials;
  yield put(startSubmit("signinForm"));

  try {
    const user = yield firebase.auth().signInWithEmailAndPassword(email, password);
    yield take("FIREBASE_AUTH_DONE");
    yield mixpanel.track("login with email and password");
    yield put(push("/"));
  } catch (error) {
    yield put(stopSubmit("signinForm", { _error: error.message }));
  }
}

function* handleSignupWithEmailAndPasswordRequest({ userData: { firstName, lastName, email, password } }) {
  const request = UserApi.getSignupUserUrl({ displayName: `${firstName} ${lastName}`, email, password });
  yield put(startSubmit("signupForm"));
  try {
    const { data: userDbRecord } = yield call(api.post, request.url, request.config);
    yield mixpanel.track("signup with email and password", { name: userDbRecord.name, uid: userDbRecord.uid });
    if (userDbRecord.token) {
      const user = yield firebase.auth().signInWithCustomToken(userDbRecord.token);
      yield put(setRawUser(user));
      yield put(push("/"));
    } else {
      yield put(push("/signin"));
    }
  } catch (error) {
    yield put(stopSubmit("signupForm", { _error: error.response.data.error }));
  }
}

function* handleSendPasswordResetEmailRequest({ email }) {
  yield put(startSubmit("forgetPasswordForm"));
  try {
    yield firebase.auth().sendPasswordResetEmail(email);
    yield put(showAlert("Check your email"));
    yield mixpanel.track("forgot password");
    yield put(stopSubmit("forgetPasswordForm"));
  } catch (error) {
    yield put(stopSubmit("forgetPasswordForm", { _error: error.message }));
  }
}

function* handleApplyVerifyEmailActionCode() {
  const { oobCode } = yield select(state => state.router.query);
  try {
    yield firebase.auth().applyActionCode(oobCode);
    yield put(setisVerificationCodeVerified(true));
    yield put(hideEmailVerificationBanner());
    yield mixpanel.track("email verified");
  } catch (error) {
    yield put(setisVerificationCodeVerified(false));
    console.log("error in email verified", oobCode, error);
  }
}

function* handleVerifyPasswordResetCodeRequest() {
  const { oobCode } = yield select(state => state.router.query);
  try {
    yield firebase.auth().verifyPasswordResetCode(oobCode);
  } catch (error) {
    yield put(showAlert("Sorry this password reset code is no longer valid", error));
    yield put(push("/"));
  }
}
function* handleChangePasswordRequest({ password }) {
  const { oobCode } = yield select(state => state.router.query);
  yield put(startSubmit("resetPasswordForm"));
  try {
    yield firebase.auth().confirmPasswordReset(oobCode, password);
    yield put(showAlert("You can now use the new password to sign in"));
    yield put(stopSubmit("resetPasswordForm"));
    yield mixpanel.track("reset password");
    yield put(push("/signin"));
  } catch (error) {
    yield put(stopSubmit("resetPasswordForm", { _error: error.response.data.error }));
  }
}

function* handleSignoutRequest() {
  yield firebaseapp.auth().signOut();
}

function* forceRefreshToken() {
  while (true) {
    //wait for 10 minutes
    yield delay(1000 * 60 * 10);
    //force refresh
    yield refreshToken(true);
  }
}

function* storeInitialReferrerData() {
  try {
    const action = yield take("ROUTER_LOCATION_CHANGED");
    let { payload: { query } = {} } = action;

    if (Object.keys(query).length > 0) {
      if (document.referrer && !document.referrer.includes("casemasters-a1545")) {
        query = { ...query, referrer: document.referrer };
      }
      query = Object.keys(query).reduce((acc, curr) => ({ ...acc, [`cm_${curr}`]: query[curr] }), {});
      localStorage.setItem("utmParamters", JSON.stringify(query));

      mixpanel.track("utmParams.read", {
        params: query,
      });
    }
  } catch (error) {
    let openUrl = "";
    if (IS_CLIENT_RENDERED) {
      openUrl = window.location && window.location.href;
    }

    mixpanel.track("utmParams.error", {
      error,
      openUrl,
    });
  }
}

function* handleEmailVerificationRequests({ successMessage, hideVerificationEmailBanner }) {
  const verificationMessage = successMessage ? successMessage : "Check your email";

  try {
    yield sendFirebaseVerificationEmail();
    yield put(showAlert(verificationMessage));
    if (hideVerificationEmailBanner) {
      yield put(hideEmailVerificationBanner());
    }
  } catch (error) {
    yield put(showAlert("Sorry, Couldn't send the confirmation email", "error"));
  }
}

function* handleCreateProfileRequests({ profileData, handleSubmitErrors }) {
  yield put(isCreatingProfile(true));
  const loggedInUser = yield select(selectUser);
  const request = UserApi.getCreateUserProfileUrl(profileData);
  try {
    yield call(api.post, request.url, request.config);
    yield put(isCreatingProfile(false));
    if (loggedInUser.email && loggedInUser.email !== profileData.email) {
      yield put(sendVerificationEmail("We sent you a verification email", false));
    }
    yield put(hideOnBoardingDimmer());
    yield put(setUser({ ...loggedInUser, ...profileData }));
    mixpanel.track("handle.onboarding.submit.success", profileData);
    const { speciality, country, email } = profileData;
    mixpanel.people.set({
      speciality,
      country,
      $email: email,
    });
  } catch (err) {
    yield put(isCreatingProfile(false));
    handleSubmitErrors(err);
  }
}

function* handleUpdateProfileRequests({ profileData, handleSubmitErrors }) {
  yield put(isCreatingProfile(true));
  const loggedInUser = yield select(selectUser);
  const request = UserApi.getUpdateUserProfileUrl(profileData);
  try {
    yield call(api.put, request.url, request.config);
    yield put(isCreatingProfile(false));
    if (loggedInUser && loggedInUser.email !== profileData.email) {
      yield put(sendVerificationEmail());
    }
    yield put(hideProfileUpdateDimmer());
    yield put(setUser({ ...loggedInUser, ...profileData }));
    mixpanel.track("handle.profileUpdate.submit.success", profileData);
    const { speciality, country, email } = profileData;
    mixpanel.people.set({
      speciality,
      country,
      $email: email,
    });
  } catch (err) {
    yield put(isCreatingProfile(false));
    handleSubmitErrors(err);
  }
}

function* handleFetchProfileDataRequests({ userId }) {
  const request = UserApi.getFetchUserProfileUrl(userId);
  try {
    const userProfile = yield call(api.get, request.url);
    const loggedInUser = yield select(selectUser);
    const onboardingDisplayFields = [ONBOARDING_FIELDS.COUNTRY, ONBOARDING_FIELDS.SPECIALITY];
    const { provider } = loggedInUser;
    if (!loggedInUser.email || !userProfile.data) {
      if (provider === "facebook.com") {
        yield put(showOnBoardingDimmer());
      } else {
        yield put(showOnBoardingDimmer({ onboardingDisplayFields }));
      }
      return;
    }
    const { country, speciality } = userProfile.data;

    const fullProfile = {
      ...loggedInUser,
      country,
      speciality,
    };
    yield put(setUser(fullProfile));
    if (!speciality || !country) {
      yield put(showProfileUpdateDimmer());
    }
  } catch (err) {
    console.error(err);
  }
}

export function* shouldWaitOnAuthDone() {
  const userProfile = yield select(selectUser);
  const isSSRUser = yield select(selectIsSSRUser);
  return IS_CLIENT_RENDERED && (userProfile === undefined || isSSRUser);
}

export function* watchAuthRequests() {
  yield takeLatest(`SET_RAW_USER`, handleSetRawUser);
  yield takeLatest(`${LOGIN_WITH_FACEBOOK_ACTION_BASE}_REQUEST`, handleLoginWithFacebookRequest);
  yield takeLatest(`${LOGIN_WITH_EMAIL_AND_PASSWORD_ACTION_BASE}_REQUEST`, handleLoginWithEmailAndPasswordRequest);
  yield takeLatest(`${SIGNUP_WITH_EMAIL_AND_PASSWORD_ACTION_BASE}_REQUEST`, handleSignupWithEmailAndPasswordRequest);
  yield takeLatest(`${SEND_PASSWORD_RESET_EMAIL_ACTION_BASE}_REQUEST`, handleSendPasswordResetEmailRequest);
  yield takeLatest(`${APPLY_VERIFY_EMAIL_ACTION_CODE_ACTION_BASE}_REQUEST`, handleApplyVerifyEmailActionCode);
  yield takeLatest(`${VERIFY_PASSWORD_RESET_CODE_ACTION_BASE}_REQUEST`, handleVerifyPasswordResetCodeRequest);
  yield takeLatest(`${CHANGE_PASSWORD_ACTION_BASE}_REQUEST`, handleChangePasswordRequest);
  yield takeLatest(`${SIGNOUT_ACTION_BASE}_REQUEST`, handleSignoutRequest);
  yield takeLatest(`${SEND_VERIFICATION_EMAIL_ACTION_BASE}_REQUEST`, handleEmailVerificationRequests);
  yield takeLatest(`${CREATE_PROFILE_ACTION_BASE}_REQUEST`, handleCreateProfileRequests);
  yield takeLatest(`${UPDATE_PROFILE_ACTION_BASE}_REQUEST`, handleUpdateProfileRequests);
  yield takeLatest(`${FETCH_PROFILE_ACTION_BASE}_REQUEST`, handleFetchProfileDataRequests);
  yield fork(forceRefreshToken);
  yield fork(storeInitialReferrerData);
}



// WEBPACK FOOTER //
// ./src/sagas/AuthSaga.js